import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { LuWrench, LuPackage, LuWandSparkles } from 'react-icons/lu';
import { Label } from './ServicioUI';
import FotosEquipo from './FotosEquipo';
import { filtroMultiTermino } from '../../utils/busqueda';
import { limpiarSerie } from '../../utils/serie';
import { TRABAJOS_FRECUENTES, sumarTrabajo, tieneTrabajo } from '../../utils/trabajosFrecuentes';

// ¿El N/S lo generó la app? Marcado al generarlo, o con la forma de los generados
// (inicial + "S" + ddmmaa + letra opcional, ej. LS211026, MS211026A)
export const nsAutomatico = (it) => !!(it?.nsGenerado || /^[A-Z]S\d{6}[A-Z]{0,2}$/.test(String(it?.equipoSerial || '').toUpperCase()));

export const inputTicketCls = `
    w-full block px-3.5 py-2.5 rounded-xl text-body font-medium outline-none
    bg-[#E8E5E0] dark:bg-[#161615] text-ink
    border border-black/10 dark:border-white/[0.08] placeholder-muted
    focus:border-[#D13A28] dark:focus:border-[#E8422F]
    focus:ring-2 focus:ring-[#D13A28]/20 transition-all
`;

/**
 * La tarjeta "Equipo N" del ticket (21-oct-2026): la MISMA para el admin (armar o editar un
 * trabajo) y para el técnico (cerrar la visita o sumar equipos), así se carga igual en los dos
 * roles. Lo que cambia va por props: el admin pasa el bloque de precio (`precio`), la calculadora
 * (`extra`) y el historial del equipo; el técnico no ve precios.
 */
export default function FormEquipoTicket({
    numero, itemActual, setItemActual, selectStyles,
    opcionesSerial = [], onElegirSerie, onCrearSerie, onGenerarSerie, generandoSerie = false,
    mostrarEquipo, setMostrarEquipo, historial = null,
    onAbrirRepuestos, precioRepuesto = null,
    precio = null, extra = null,
    ayudaFotos = null,
    anterior = null, extraCopia = null,
    onAgregar, textoAgregar = '+ Agregar equipo al ticket', autoFocus = true,
}) {
    const reps = itemActual.repuestosUsados || [];
    // Fotos en todos los equipos, también antes de poner el N/S (21-oct-2026: el equipo 2 no las mostraba).
    // Solo no van en lo que no es un equipo (mostrador, producto suelto).
    const sinEquipo = ['MOSTRADOR', 'SIN-SN'].includes(String(itemActual.equipoSerial || '').toUpperCase());
    // "Igual que el anterior" (21-oct-2026, reemplaza la Carga rápida): copia el trabajo y los repuestos
    // del equipo de arriba (y el precio en el admin); queda cambiar el N/S y sacar las fotos.
    const copiarAnterior = () => {
        if (!anterior) return;
        setItemActual(it => ({
            ...it,
            trabajo: anterior.trabajo || '',
            repuestosUsados: (anterior.repuestosUsados || []).map(r => ({ ...r })),
            ...(extraCopia ? extraCopia(anterior) : {}),
        }));
        setMostrarEquipo(true);
        // Si el anterior llevó N/S automático, este también (queda editable en el campo N/S)
        if (nsAutomatico(anterior) && !itemActual.equipoSerial && onGenerarSerie) onGenerarSerie();
    };
    return (
        <div className="rounded-2xl p-4 bg-panel border border-black/[0.07] dark:border-white/[0.07]">
            <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-label font-black bg-brand-red">
                    {numero}
                </div>
                <p className="flex-1 text-label font-bold uppercase tracking-widest text-muted">Equipo {numero}</p>
            </div>
            {anterior && (
                <button type="button" onClick={copiarAnterior}
                    className="w-full mb-4 min-h-11 px-3 py-2 rounded-xl bg-chip text-left active:scale-[0.98] transition-all">
                    <span className="block text-body font-black text-ink">Igual que el anterior</span>
                    <span className="block text-caption text-muted truncate">{[anterior.equipoSerial, anterior.trabajo].filter(Boolean).join(' · ') || 'Copia el trabajo y los repuestos'}</span>
                </button>
            )}

            <div className="flex flex-col gap-3">
                <div>
                    <Label>Descripción del trabajo</Label>
                    {/* Trabajos más comunes: un toque y queda escrito; otro toque lo saca */}
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {TRABAJOS_FRECUENTES.map(t => {
                            const on = tieneTrabajo(itemActual.trabajo, t);
                            return (
                                <button key={t} type="button" aria-pressed={on}
                                    onClick={() => setItemActual(it => ({ ...it, trabajo: sumarTrabajo(it.trabajo, t).slice(0, 400) }))}
                                    className={`h-9 px-3 rounded-full text-label font-bold active:scale-95 ${on ? 'bg-brand-red text-white' : 'bg-chip text-secondary'}`}>{t}</button>
                            );
                        })}
                    </div>
                    <textarea className={`${inputTicketCls} resize-none min-h-[80px]`}
                        placeholder="Describí el trabajo realizado o a realizar..."
                        maxLength={400} autoFocus={autoFocus && !!itemActual.trabajo}
                        value={itemActual.trabajo || ''}
                        onChange={e => setItemActual({ ...itemActual, trabajo: e.target.value })} />
                    <div className="flex justify-end mt-1">
                        <span className={`text-caption font-bold ${(itemActual.trabajo || '').length >= 380 ? 'text-[#D13A28]' : (itemActual.trabajo || '').length >= 300 ? 'text-[#D48800]' : 'text-muted'}`}>
                            {(itemActual.trabajo || '').length}/400
                        </span>
                    </div>
                </div>

                {precio}

                <div className="border-t border-black/[0.07] dark:border-white/[0.07] pt-3 space-y-1.5">
                    {/* Equipo */}
                    <button type="button" onClick={() => setMostrarEquipo(v => !v)}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-label font-bold bg-chip text-secondary active:scale-[0.98] transition-all">
                        <span className="inline-flex items-center gap-1.5"><LuWrench size={13} />{itemActual.equipoSerial ? ` ${itemActual.equipoSerial}` : ' Equipo (S/N, modelo, ubicación)'}</span>
                        <span className="text-label">{mostrarEquipo ? '▲' : '▼'}</span>
                    </button>
                    {mostrarEquipo && (
                        <div className="space-y-3 p-3 rounded-xl bg-[#E8E5E0]/50 dark:bg-[#2A2A28]/50">
                            <div>
                                <Label>N/S Dispenser</Label>
                                <CreatableSelect filterOption={filtroMultiTermino} styles={selectStyles} menuPosition="fixed" menuPlacement="auto" menuPortalTarget={document.body}
                                    options={opcionesSerial}
                                    value={itemActual.equipoSerial ? { label: itemActual.equipoSerial, value: itemActual.equipoSerial } : null}
                                    onChange={s => onElegirSerie(s)}
                                    onCreateOption={val => onCrearSerie(limpiarSerie(val))}
                                    isClearable placeholder="Buscar o escribir S/N..." noOptionsMessage={() => 'Escribí el S/N manualmente'} />
                                <button type="button" onClick={onGenerarSerie} disabled={generandoSerie}
                                    className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-label font-bold bg-chip text-secondary active:scale-[0.98] transition-all disabled:opacity-50">
                                    <LuWandSparkles size={13} /> {generandoSerie ? 'Generando...' : 'Generar N/S automático'}
                                </button>
                                {!itemActual.equipoSerial && (
                                    <p className="mt-1.5 text-caption text-muted">El N/S se carga una sola vez y queda en el equipo. Generalo solo si el equipo no tiene (instalación nueva). Si ahora no lo sabés, dejalo vacío: se carga al cerrar.</p>
                                )}
                            </div>
                            {historial}
                            <input className={inputTicketCls} value={itemActual.modeloEquipo || ''}
                                onChange={e => setItemActual({ ...itemActual, modeloEquipo: e.target.value })}
                                placeholder="Modelo (ej: Bacope frío/calor)" />
                            <input className={inputTicketCls} value={itemActual.ubicacionEquipo || ''}
                                onChange={e => setItemActual({ ...itemActual, ubicacionEquipo: e.target.value })}
                                placeholder="Ubicación (ej: Piso 2, depósito)" />
                        </div>
                    )}

                    {/* Repuestos */}
                    <button type="button" onClick={onAbrirRepuestos}
                        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-label font-bold bg-chip text-secondary active:scale-[0.98] transition-all">
                        <span className="inline-flex items-center gap-1.5"><LuPackage size={13} />{reps.length > 0
                            ? ` ${reps.length} repuesto${reps.length > 1 ? 's' : ''}`
                            : ' Repuestos'}</span>
                        <span className="text-label">+</span>
                    </button>
                    {reps.length > 0 && (
                        <div className="rounded-xl overflow-hidden bg-chip border border-black/10 dark:border-white/10">
                            {reps.map((r, i) => (
                                <div key={i} className={`px-3 py-2 flex items-center gap-3 ${i < reps.length - 1 ? 'border-b-[0.5px] border-black/[0.07]' : ''}`}>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-bold text-body truncate text-ink">{r.nombre}</p>
                                    </div>
                                    <span className="text-caption font-black text-muted">{r.cantidad}x</span>
                                    {precioRepuesto && <p className="font-black text-body text-ink">${Math.round(precioRepuesto(r)).toLocaleString('es-AR')}</p>}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Fotos del equipo: hasta 4, cada una con su rótulo */}
                    {!sinEquipo && (
                        <div className="space-y-1.5">
                            <p className="text-label font-black uppercase tracking-widest text-muted">Fotos <span className="normal-case tracking-normal font-bold">(opcional)</span></p>
                            {ayudaFotos && <p className="text-caption text-muted">{ayudaFotos}</p>}
                            <FotosEquipo fotoAntes={itemActual.fotoAntes} fotoDespues={itemActual.fotoDespues} fotosExtra={itemActual.fotosExtra || []}
                                fotosRotulos={itemActual.fotosRotulos || null} onChange={c => setItemActual(prev => ({ ...prev, ...c }))} />
                        </div>
                    )}

                    {extra}
                </div>

                <button type="button" onClick={onAgregar}
                    className="w-full py-3.5 rounded-xl font-black text-sm text-white active:scale-[0.98] transition-all bg-brand-red">
                    {textoAgregar}
                </button>
            </div>
        </div>
    );
}
